import React, { useState, useRef, useEffect } from "react";
import cx from "classnames";
import s from "./signatureManager.scss";
import { Icon, trash, edit } from "Icons/index";
import Select from "Components/select/Select";
import Toggle from "Components/toggle/Toggle";
import Input from "Components/input/Input";
import Button from "Components/button/Button";
import ButtonIcon from "Components/buttonIcon/ButtonIcon";
import RichEditor from "Components/richEditor/RichEditor";
import t from "Utils/translation";
import DropdownIndicator from "Components/customArrowSelect/CustomArrowSelect";
import QboxmailApi from "@/qboxmailApi";
import WarningBanner from "Components/warningBanner/WarningBanner";
import { alertError, alertSuccess } from "@/utils/alerts";
import { bytesToHuman } from "@/utils/bytesFormats";

import { signatureBytesLimit } from "@/config";

const selectFormattedIdentities = (identities, signatures) => {
  let signaturesIdentities = signatures.map((sig) => sig.identity);

  let result = [];

  let all = [];

  for (let identity of identities) {
    all.push({ value: identity.address, label: identity.address });
    if (signaturesIdentities.indexOf(identity.address) === -1) {
      result.push({ value: identity.address, label: identity.address });
    }
  }

  return {
    all: all,
    noSignature: result,
  };
};

const getNameFromEmail = (emailAccount) => {
  let result = emailAccount.firstname;
  if (emailAccount.lastname) result = result + ` ${emailAccount.lastname}`;
  return result;
};

const api = new QboxmailApi({ useCookie: true });

// eslint-disable-next-line react/display-name
export const SignatureManager = (props) => {
  const [signatureOpened, setSignatureOpened] = useState(null);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState({});
  const [error, setError] = useState();

  // local signature form state
  const [title, setTitle] = useState();
  const [defaultSignature, setDefaultSignature] = useState(false);
  const [senderInBcc, setSenderInBcc] = useState(false);
  const [onlyNewMessages, setOnlyNewMessages] = useState(false);
  const [identity, setIdentity] = useState();

  // new signature state
  const [newSignature, setNewSignature] = useState(null);

  let editorRef = useRef(null);

  // fill the editor when user select another signature
  useEffect(() => {
    if (!editorRef.current) return; // first run, do nothing

    if (signatureOpened) {
      editorRef.current.setContent(signatureOpened.body);
    } else {
      editorRef.current.setContent("");
    }
  }, [signatureOpened]);

  const toggleSignature = (signature) => {
    setError(null);

    if (signatureOpened?.identity === signature?.identity) {
      setSignatureOpened(null);
      setNewSignature(null);
      return;
    }

    setSignatureOpened(signature);

    if (signature) {
      setIdentity(signature.identity);
      setTitle(signature.title);
      setDefaultSignature(signature.defaultSignature);
      setSenderInBcc(signature.senderInBcc);
      setOnlyNewMessages(signature.onlyNewMessages);
    }
  };

  const addSignature = (e) => {
    e.preventDefault();

    let newSignature = {
      title: "Nuova firma",
      identity: "",
    };

    setNewSignature(newSignature);
    toggleSignature(newSignature);

    if (props.emailAccount.identities.length === 1) {
      // if has only one identity
      setTitle(getNameFromEmail(props.emailAccount));
      setIdentity(props.emailAccount.identities[0].address);
    }
  };

  const pickIdentity = (v) => {
    setIdentity(v.identity);

    // reset title if is adding a new signature
    if (newSignature) {
      setTitle(getNameFromEmail(props.emailAccount));
    }
  };

  const remove = async (identity) => {
    setDeleting({ ...deleting, [identity]: true });

    api
      .deleteEmailSignature(props.emailAccount.code, identity)
      .then(() => {
        if (props.onChange) props.onChange();
        toggleSignature();
        alertSuccess(t("alert.accountUpdatedSuccess"));
      })
      .catch(async (err) => {
        console.log("Error while deleting signture", err);
        if (err.status === 403) {
          let errMsg = await err.json();
          setError(errMsg.message);
        } else if (err.status === 500 || err.status === 404) {
          setError("Error performing the operation, contact the support.");
        }
      })
      .finally(() => {
        setDeleting({ ...deleting, [identity]: false });
      });
  };

  const save = async (e) => {
    e.preventDefault();
    const html = editorRef.current.getContent().html;

    if (html.length > signatureBytesLimit) {
      window.alert(
        t("signature.maxSize", null, { size: bytesToHuman(html.length) }) + bytesToHuman(signatureBytesLimit),
      );
      return;
    }

    setSaving(true);

    let apiCall = newSignature ? "createEmailSignature" : "editEmailSignature";

    api[apiCall](props.emailAccount.code, {
      body: html,
      identity: identity,
      title: title,
      default_signature: defaultSignature,
      sender_in_bcc: senderInBcc,
      only_new_messages: onlyNewMessages,
    })
      .then(() => {
        alertSuccess(t("alert.accountUpdatedSuccess"));
        toggleSignature();
        setNewSignature(null);

        if (props.onChange) props.onChange();
      })
      .catch(async (err) => {
        console.log("Error while saving signture", err);
        if (err.status === 403) {
          let errMsg = await err.json();
          setError(errMsg.message);
        } else if (err.status === 500 || err.status === 404) {
          setError("Error performing the operation, contact the support.");
        }
      })
      .finally(() => {
        setSaving(false);
      });
  };

  let tableRows = [...props.signatures];
  if (newSignature) {
    tableRows = [...props.signatures, newSignature];
  }

  return (
    <div className={cx(props.className, { [s.disabled]: props.disabled })}>
      <div className={s.list}>
        {/*title bar*/}
        <div className={s.listHeader}>
          <div>{t("listTable.header.title.signature.displayName")}</div>
          <div>{t("listTable.header.title.signature.identity")}</div>
        </div>

        {tableRows.length === 0 && (
          <div className={s.listRow}>
            <div className={s.listPlaceholder}>{t("listTable.placeholder.signature")}</div>
          </div>
        )}

        {/*rows*/}
        {tableRows.map((signature) => (
          <div
            className={s.listRow}
            style={{
              backgroundColor: signatureOpened?.identity === signature.identity ? "var(--bg-end)" : "var(--bg-start)",
            }}
            key={signature.identity}
          >
            <div className={s.listItem}>
              <div>{signature.title}</div>
              <div className={s.identity}>{signature.identity}</div>
              <div>
                {signature.defaultSignature && <span className={s.tagDefault}>{t("tag.signature.default")}</span>}
                {signature.senderInBcc && <span className={s.tagCcn}>{t("tag.signature.bcc")}</span>}
              </div>

              <div className={s.listActionRight}>
                {signatureOpened?.identity !== signature.identity && (
                  <ButtonIcon icon={edit} title={t("buttonIcons.edit")} onClick={() => toggleSignature(signature)} />
                )}

                {(signatureOpened?.identity !== signature.identity || !newSignature) && (
                  <ButtonIcon
                    icon={trash}
                    title={t("buttonIcons.delete")}
                    onClick={() => remove(signature.identity)}
                    loading={deleting[signature.identity]}
                  />
                )}
              </div>
            </div>

            {/*full signature info*/}
            {signatureOpened?.identity === signature.identity && (
              <div className={s.listBox}>
                <div className={s.listBoxContent}>
                  {props.emailAccount.identities.length > 1 && (
                    <Select
                      className={s.listInput}
                      label={t("input.label.select.identity")}
                      disabled={signatureOpened?.identity !== ""}
                      components={{ DropdownIndicator }}
                      name="identity"
                      errors={false}
                      options={
                        newSignature
                          ? selectFormattedIdentities(props.emailAccount.identities, props.signatures).noSignature
                          : selectFormattedIdentities(props.emailAccount.identities, props.signatures).all
                      }
                      onChange={pickIdentity}
                      value={signatureOpened?.identity}
                      clearable={false}
                    />
                  )}

                  <Input
                    className={s.listInput}
                    label={t("input.label.display.name")}
                    name="title"
                    type="text"
                    id="repeater"
                    onChange={(v) => setTitle(v.title)}
                    value={title}
                    disabled={props.emailAccount.email_address === identity}
                  />

                  <div className={s.listInput}>
                    <RichEditor
                      ref={editorRef}
                      id="signature"
                      label={t("input.label.signature.text")}
                      maxImageBytes={150000}
                    />
                  </div>

                  <Toggle
                    className={s.listInput}
                    label={t("input.label.default.identity")}
                    checked={defaultSignature}
                    onChange={(v) => setDefaultSignature(v.defaultSignature)}
                    name="defaultSignature"
                  />

                  <Toggle
                    className={s.listInput}
                    label={t("input.label.add.sender.bcc")}
                    checked={senderInBcc}
                    onChange={(v) => setSenderInBcc(v.senderInBcc)}
                    name="senderInBcc"
                  />

                  <Toggle
                    className={s.listInput}
                    label={t("input.label.add.signature.replies")}
                    checked={onlyNewMessages}
                    onChange={(v) => setOnlyNewMessages(v.onlyNewMessages)}
                    name="onlyNewMessages"
                  />

                  {error && <WarningBanner msg={error} color="red" />}

                  <footer className={s.listBoxFooter}>
                    <Button color="text" onClick={() => toggleSignature(signature)}>
                      button.cancel
                    </Button>

                    <Button loading={saving} disabled={!identity} onClick={save}>
                      button.save
                    </Button>
                  </footer>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>

      {selectFormattedIdentities(props.emailAccount.identities, props.signatures).noSignature.length > 0 &&
        !newSignature && (
          <Button className={s.listAddBtn} onClick={addSignature} color="outline" disabled={props.disabled}>
            button.new.signature
          </Button>
        )}
    </div>
  );
};
